import * as Sentry from '@sentry/react'
import { getLastSupabaseError } from '../supabase/queries/logSupabaseError'
import { db } from '../db/indexeddb'
import type { SyncQueueItem } from '../db/models'

/**
 * Sync Queue Management
 * Manages pending changes that need to be synced to cloud
 */

/**
 * Nach so vielen Fehlschlägen rührt der Push einen Eintrag nicht mehr an
 * (`getPendingChanges` blendet ihn aus). Exportiert, weil das Sicherheitsnetz
 * in `sync-repair.ts` dieselbe Grenze kennen muss: Ein ausgebrannter Eintrag
 * WARTET nicht mehr, er ist tot — und die Zeile dahinter braucht einen neuen.
 */
export const MAX_RETRIES = 5

/**
 * Add change to sync queue
 */
export async function queueChange(
  action: 'create' | 'update' | 'delete',
  table: SyncQueueItem['table'],
  recordId: number,
  data: any
): Promise<void> {
  try {
    await db.syncQueue.add({
      action,
      table,
      recordId,
      data,
      synced: false,
      timestamp: new Date(),
      retryCount: 0,
    })

  } catch (error) {
    console.error('[SyncQueue] Failed to queue change:', error)
  }
}

/**
 * **Fasst zusammen, was dieselbe Zeile meint** — rein, ohne Datenbank.
 *
 * `queueSyncChange` reiht je Schreibvorgang einen Eintrag ein und
 * dedupliziert nicht. Drei Tipp-Pausen im Titel einer Aufgabe sind drei
 * Einträge; hochgeladen wird dabei nie ein Zwischenstand (der Push liest die
 * Entität frisch, der Eintrag trägt nur die Kennung), wohl aber dreimal
 * derselbe Endstand — drei Netzwerkrunden, drei `updated_at`-Stempel, drei
 * Realtime-Ereignisse auf dem zweiten Gerät.
 *
 * **Die Regel:** Nach `table` + `data.localId` gruppiert und in zeitlicher
 * Reihenfolge überlebt von mehreren AUFEINANDERFOLGENDEN Einträgen derselben
 * Handlung der jüngste.
 *
 * ```
 * update, update, update  →  update₃
 * create, update, update  →  create, update₃
 * update, delete, update  →  update₁, delete, update₃   (nichts fällt weg)
 * ```
 *
 * **Nur aufeinanderfolgend.** Ein `update` vor einem `delete` und eines
 * danach sind zwei verschiedene Absichten; zusammengefasst hätte das Löschen
 * das letzte Wort, obwohl danach noch geschrieben wurde.
 *
 * **Nur mit Kennung.** Ohne `data.localId` ist nicht zu sagen, welche Zeile
 * gemeint ist — alle unter einen Schlüssel zu werfen träfe fremde Datensätze.
 *
 * Der JÜNGSTE überlebt: Er trägt den `retryCount`, den der Push
 * weiterzählt, und bei `delete` die zuletzt bekannte Cloud-Kennung.
 *
 * @param items Einträge in zeitlicher Reihenfolge.
 * @returns `changes` — was der Push sehen soll; `superseded` — die überholten.
 */
export function collapseSupersededChanges(
  items: SyncQueueItem[],
): { changes: SyncQueueItem[]; superseded: SyncQueueItem[] } {
  const changes: SyncQueueItem[] = []
  const superseded: SyncQueueItem[] = []
  /** Je Zeile der zuletzt behaltene Eintrag — sein Platz in `changes`. */
  const lastPerRow = new Map<string, { index: number; action: string }>()

  for (const item of items) {
    const localId = item.data?.localId
    if (localId == null) {
      changes.push(item)
      continue
    }

    const key = `${item.table}\u0000${localId}`
    const last = lastPerRow.get(key)
    if (last && last.action === item.action) {
      // Derselbe Vorsatz, nur später gefasst: Der ältere fährt nicht mit.
      superseded.push(changes[last.index])
      changes[last.index] = item
      continue
    }

    lastPerRow.set(key, { index: changes.length, action: item.action })
    changes.push(item)
  }

  return { changes, superseded }
}

/**
 * Get pending changes (excludes items that exceeded retry limit)
 *
 * **Und fasst zusammen, was dieselbe Zeile meint** (siehe
 * `collapseSupersededChanges`). Hier und nicht beim Einreihen:
 * `queueSyncChange` sitzt auf dem heißen Pfad jedes Schreibvorgangs, und ein
 * Blick in die Warteschlange bräuchte dort einen vollen Tabellendurchlauf —
 * `data.localId` ist verschachtelt und damit nicht indiziert, und der
 * `synced`-Index ist nutzlos, weil IndexedDB keine Booleans als Schlüssel
 * zulässt (deshalb steht unten auch `.filter` und nicht `.where`). An DIESER
 * Stelle liegt die Liste ohnehin vollständig und sortiert vor.
 *
 * Die überholten Einträge werden gestempelt und nicht gelöscht — wie jeder
 * erledigte räumt `clearSyncedItems` sie nach sieben Tagen ab.
 */
export async function getPendingChanges(): Promise<SyncQueueItem[]> {
  const pending = await db.syncQueue
    .filter((item) => item.synced === false && (item.retryCount ?? 0) < MAX_RETRIES)
    .sortBy('timestamp')

  const { changes, superseded } = collapseSupersededChanges(pending)
  if (superseded.length > 0) {
    await db.syncQueue.bulkUpdate(
      superseded.map((item) => ({ key: item.id!, changes: { synced: true } })),
    )
  }
  return changes
}

/**
 * Mark change as synced
 */
export async function markAsSynced(queueItemId: number): Promise<void> {
  await db.syncQueue.update(queueItemId, { synced: true })
}

/**
 * Mark change as failed and increment retry count.
 * Items that exceed MAX_RETRIES are logged and will no longer be retried.
 */
export async function markAsFailed(queueItemId: number, error: string): Promise<void> {
  const item = await db.syncQueue.get(queueItemId)
  const retryCount = (item?.retryCount ?? 0) + 1

  await db.syncQueue.update(queueItemId, {
    error,
    synced: false,
    retryCount,
  })

  /*
    **Beim Verbrennen Alarm schlagen — genau einmal.**

    Ab hier ist der Eintrag für den Push unsichtbar (`getPendingChanges`
    blendet ihn aus), und `retryFailedItems`, das ihn zurückholen würde, ruft
    niemand auf. Seine Zeile geht also nie wieder hoch: kein Fehlversuch mehr,
    sondern ein dauerhaft verlorener Datensatz.

    Warum Sentry und nicht bloß die Konsole: Am 07.09.2026 traf das 39
    Notizen, weil eine Cloud-Migration nicht eingespielt war. Jeder Insert
    wurde abgelehnt, nach fünf Anläufen war Ruhe — lautlos. Aufgefallen ist es
    erst Tage später, als die Zeilen auf dem zweiten Gerät fehlten. Bei einem
    Nutzer hätte es niemand je bemerkt.

    `=== MAX_RETRIES`, nicht `>=`: Ein ausgebrannter Eintrag kann weiter
    angefasst werden, und jeder weitere Fehlschlag wäre dieselbe Nachricht.
    Gemeldet wird der Übergang, nicht der Zustand.

    Gewöhnliche Fehlversuche (Netz weg, Server kurz launisch) bleiben bewusst
    stumm — die sind normal und würden nur rauschen.
  */
  if (retryCount === MAX_RETRIES) {
    const detail = `[SyncQueue] Item ${queueItemId} permanently failed after ${MAX_RETRIES} retries: ${error}`
    console.error(detail)
    Sentry.captureMessage('[Sync] Queue item permanently failed — row will never reach the cloud', {
      level: 'error',
      tags: {
        source: 'sync-queue',
        kind: 'retry-exhausted',
        table: item?.table ?? 'unknown',
        action: item?.action ?? 'unknown',
      },
      extra: {
        queueItemId,
        localId: item?.data?.localId ?? null,
        error,
        retryCount,
        // Der eigentliche Grund: Code und Text von Supabase (siehe `getLastSupabaseError`).
        supabaseError: getLastSupabaseError(),
      },
    })
  } else if (retryCount > MAX_RETRIES) {
    console.error(`[SyncQueue] Item ${queueItemId} still failing past its limit: ${error}`)
  }
}

/**
 * Clear synced queue items (cleanup)
 */
export async function clearSyncedItems(olderThanDays = 7): Promise<number> {
  try {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - olderThanDays)

    const deleted = await db.syncQueue
      .filter((item) => item.synced === true && item.timestamp < cutoffDate)
      .delete()

    return deleted
  } catch (error) {
    console.warn('[SyncQueue] clearSyncedItems failed:', error)
    return 0
  }
}

/**
 * Get sync queue statistics
 */
export async function getSyncQueueStats() {
  const allItems = await db.syncQueue.toArray()

  const pending = allItems.filter((item) => !item.synced && !item.error).length
  const synced = allItems.filter((item) => item.synced).length
  const failed = allItems.filter((item) => !item.synced && !!item.error).length

  return {
    pending,
    synced,
    failed,
    total: allItems.length,
  }
}

/**
 * Retry failed sync items (resets retry count)
 */
export async function retryFailedItems(): Promise<SyncQueueItem[]> {
  const failedItems = await db.syncQueue
    .filter((item) => !item.synced && !!item.error)
    .toArray()

  // Clear error and reset retry count
  await Promise.all(
    failedItems.map((item) =>
      db.syncQueue.update(item.id!, {
        error: undefined,
        synced: false,
        retryCount: 0,
      })
    )
  )

  return failedItems
}
